// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// D6 C6.5: a read-only browse modal over the content library. Lists templates,
// archetypes, and sectors from the public read API (/api/v1/...), previews each
// (name + description), and lets the user choose a template -- which opens New
// Project seeded with it via /new?template=<slug> (the slug rides the URL so the
// choice survives any route remount; NewProject reads it on mount). Authoring
// (create/edit) is API-only for v1.

import * as React from 'react';
import { Link } from 'wouter';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Card, CardContent } from '@simlin/diagram';

import { fetchContentList, TemplateSummary } from './content-client';

import styles from './ContentLibrary.module.css';
import typography from './typography.module.css';

interface ContentLibraryProps {
  open: boolean;
  onClose: () => void;
}

interface ContentLibraryState {
  templates: TemplateSummary[];
  archetypes: TemplateSummary[];
  sectors: TemplateSummary[];
  loaded: boolean;
}

export class ContentLibrary extends React.Component<ContentLibraryProps, ContentLibraryState> {
  state: ContentLibraryState = { templates: [], archetypes: [], sectors: [], loaded: false };

  componentDidUpdate(prevProps: ContentLibraryProps): void {
    // Lazy-load the first time the modal opens (don't hit the API on mount).
    if (this.props.open && !prevProps.open && !this.state.loaded) {
      void this.load();
    }
  }

  load = async (): Promise<void> => {
    const [templates, archetypes, sectors] = await Promise.all([
      fetchContentList('templates'),
      fetchContentList('archetypes'),
      fetchContentList('sectors'),
    ]);
    this.setState({ templates, archetypes, sectors, loaded: true });
  };

  renderSection(title: string, items: TemplateSummary[], startable: boolean): React.ReactNode {
    if (items.length === 0) {
      return null;
    }
    return (
      <section className={styles.section}>
        <h3 className={typography.heading6}>{title}</h3>
        <div className={styles.grid}>
          {items.map((item) => (
            <Card key={item.slug} variant="outlined" className={styles.card}>
              <CardContent>
                <h4 className={styles.cardTitle}>{item.name}</h4>
                {item.description ? <p className={styles.cardDesc}>{item.description}</p> : null}
                {startable ? (
                  <Link
                    to={`/new?template=${encodeURIComponent(item.slug)}`}
                    className={styles.useLink}
                    onClick={this.props.onClose}
                  >
                    Use this template &rarr;
                  </Link>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  render(): React.JSX.Element {
    const { templates, archetypes, sectors, loaded } = this.state;
    const empty = loaded && templates.length === 0 && archetypes.length === 0 && sectors.length === 0;
    return (
      <Dialog open={this.props.open} onClose={this.props.onClose} className={styles.dialog}>
        <DialogTitle>Content Library</DialogTitle>
        <DialogContent>
          {!loaded ? <p className={styles.muted}>Loading&hellip;</p> : null}
          {empty ? <p className={styles.muted}>No content available yet.</p> : null}
          {this.renderSection('Templates', templates, true)}
          {this.renderSection('Archetypes', archetypes, false)}
          {this.renderSection('Sectors', sectors, false)}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
